import { supabase, STAT_TYPES } from './supabase';

const TABLE_NAMES_MAP = {
  'PASSING': 'passing_stats',
  'RUSHING': 'rushing_stats',
  'RECEIVING': 'receiving_stats',
  'BLOCKING': 'blocking_stats',
  'DEFENSE': 'defense_stats',
  'KICKING': 'kicking_stats'
};

const normalizeColumnNames = (record) => {
  const normalized = {};
  const columnMappings = {
    'pos': 'POS',
    'name': 'NAME',
    'team': 'TEAM',
    'cmp': 'CMP',
    'att': 'ATT',
    'cmp_pct': 'CMP%',
    'yrds': 'YRDS',
    'td': 'TD',
    'int': 'INT',
    'pick6': 'PICK6',
    'fum': 'FUM',
    'avg': 'AVG',
    'qbr': 'QBR',
    'tds': 'TDS',
    'long': 'LONG',
    'rec': 'REC',
    'trgt': 'TRGT',
    'rec_pct': 'REC%',
    'lgo': 'LGO',
    'abv': 'ABV',
    'dp': 'DP',
    'snps': 'SNPS',
    'ska': 'SKA',
    'sad': 'SAD',
    'sas': 'SAS',
    'tck': 'TCK',
    'tfl': 'TFL',
    'sck': 'SCK',
    'sfty': 'SFTY',
    'ff': 'FF',
    'fr': 'FR',
    'frtd': 'FRTD',
    'pbu': 'PBU',
    'xpm': 'XPM',
    'xpa': 'XPA',
    'xp_pct': 'XP%',
    'fgm': 'FGM',
    'fga': 'FGA',
    'fg_pct': 'FG%',
    'k_pct': 'K%',
    'pnts': 'PNTS',
  };

  for (const [dbCol, displayCol] of Object.entries(columnMappings)) {
    if (record.hasOwnProperty(dbCol)) {
      normalized[displayCol] = record[dbCol];
    }
  }
  
  normalized.id = record.id;
  normalized.created_at = record.created_at;
  normalized.week = record.week;

  return normalized;
};

const calculatePassingStats = (player) => {
  return {
    POS: player.pos,
    NAME: player.name,
    TEAM: player.team,
    CMP: player.cmp,
    ATT: player.att,
    'CMP%': player.cmp_pct,
    YRDS: player.yrds,
    TD: player.td,
    INT: player.int,
    PICK6: player.pick6,
    FUM: player.fum,
    AVG: player.avg,
    QBR: player.qbr
  };
};

const calculateRushingStats = (player) => {
  return {
    POS: player.pos,
    NAME: player.name,
    TEAM: player.team,
    ATT: player.att,
    YRDS: player.yrds,
    TDS: player.tds,
    FUM: player.fum,
    AVG: player.avg,
    LONG: player.long
  };
};

const calculateReceivingStats = (player) => {
  return {
    POS: player.pos,
    NAME: player.name,
    TEAM: player.team,
    REC: player.rec,
    TRGT: player.trgt,
    'REC%': player.rec_pct,
    YRDS: player.yrds,
    TDS: player.tds,
    FUM: player.fum,
    AVG: player.avg,
    LONG: player.long
  };
};

const calculateBlockingStats = (player) => {
  return {
    TEAM: player.team,
    ABV: player.abv,
    DP: player.dp,
    SNPS: player.snps,
    SKA: player.ska
  };
};

const calculateKickingStats = (player) => {
  return {
    POS: player.pos,
    NAME: player.name,
    TEAM: player.team,
    XPM: player.xpm,
    XPA: player.xpa,
    'XP%': player.xp_pct,
    FGM: player.fgm,
    FGA: player.fga,
    'FG%': player.fg_pct,
    'K%': player.k_pct,
    PNTS: player.pnts,
    LONG: player.long
  };
};

const columnMappings = {
  'cmp': 'CMP',
  'att': 'ATT',
  'cmp_pct': 'CMP%',
  'yrds': 'YRDS',
  'td': 'TD',
  'int': 'INT',
  'pick6': 'PICK6',
  'fum': 'FUM',
  'avg': 'AVG',
  'qbr': 'QBR',
  'tds': 'TDS',
  'long': 'LONG',
  'rec': 'REC',
  'trgt': 'TRGT',
  'rec_pct': 'REC%',
  'lgo': 'LGO',
  'abv': 'ABV',
  'dp': 'DP',
  'snps': 'SNPS',
  'ska': 'SKA',
  'sad': 'SAD',
  'sas': 'SAS',
  'tck': 'TCK',
  'tfl': 'TFL',
  'sck': 'SCK',
  'sfty': 'SFTY',
  'ff': 'FF',
  'fr': 'FR',
  'frtd': 'FRTD',
  'pbu': 'PBU',
  'xpm': 'XPM',
  'xpa': 'XPA',
  'xp_pct': 'XP%',
  'fgm': 'FGM',
  'fga': 'FGA',
  'fg_pct': 'FG%',
  'k_pct': 'K%',
  'pnts': 'PNTS',
};

const calculateNFLQBR = (cmp, att, yrds, td, int_count) => {
  if (att === 0) return 0;

  const capComponent = (value, max = 2.375) => Math.min(Math.max(value, 0), max);

  const compComp = capComponent(((cmp / att) * 100 - 30) * 0.05);
  const yrdsComp = capComponent(((yrds / att) - 3) * 0.25);
  const tdComp = capComponent((td / att) * 20);
  const intComp = capComponent(2.375 - (int_count / att) * 25);

  const qbr = ((compComp + yrdsComp + tdComp + intComp) / 6) * 100;
  return parseFloat(qbr.toFixed(2));
};

const aggregatePlayerStats = (weeklyRecords, statType) => {
  const aggregated = {};

  weeklyRecords.forEach(record => {
    const statTypeUpper = statType ? statType.toUpperCase() : '';
    
    let keyField;
    if (statTypeUpper === 'BLOCKING') {
      keyField = (record.team || record.TEAM || '').toLowerCase();
    } else {
      keyField = (record.name || record.NAME || record.player || '').toLowerCase();
    }

    if (!aggregated[keyField]) {
      if (statTypeUpper === 'BLOCKING') {
        aggregated[keyField] = {
          TEAM: record.team || record.TEAM || 'Unknown',
          ABV: record.abv || record.ABV || ''
        };
      } else {
        aggregated[keyField] = {
          NAME: record.name || record.NAME || record.player || 'Unknown',
          TEAM: record.team || record.TEAM || '',
          POS: record.pos || record.POS || record.position || ''
        };
      }
    }

    const derivedFields = ['cmp_pct', 'rec_pct', 'xp_pct', 'fg_pct', 'k_pct', 'qbr', 'avg', 'sad', 'sas'];

    for (const dbKey in record) {
      if (statTypeUpper === 'BLOCKING') {
        if (dbKey === 'team' || dbKey === 'TEAM' || dbKey === 'abv' || dbKey === 'ABV' || dbKey === 'id' || dbKey === 'created_at' || dbKey === 'week' || dbKey === 'season_id') {
          continue;
        }
      } else {
        if (dbKey === 'name' || dbKey === 'NAME' || dbKey === 'player' || dbKey === 'id' || dbKey === 'created_at' || dbKey === 'week' || dbKey === 'team' || dbKey === 'TEAM' || dbKey === 'pos' || dbKey === 'POS' || dbKey === 'position' || dbKey === 'season_id') {
          continue;
        }
      }

      if (derivedFields.includes(dbKey.toLowerCase())) {
        continue;
      }

      const displayKey = columnMappings[dbKey] || dbKey.toUpperCase();
      const value = Number(record[dbKey]);
      
      if (!isNaN(value)) {
        aggregated[keyField][displayKey] = (aggregated[keyField][displayKey] || 0) + value;
      }
    }
  });

  const result = Object.values(aggregated);

  result.forEach(player => {
    const statTypeUpper = statType ? statType.toUpperCase() : '';

    if (statTypeUpper === 'PASSING') {
      if (player.ATT && player.ATT > 0) {
        player['CMP%'] = parseFloat(((player.CMP / player.ATT) * 100).toFixed(2));
      }
      if (player.CMP && player.ATT && player.YRDS && player.TD !== undefined && player.INT !== undefined) {
        player.QBR = calculateNFLQBR(player.CMP, player.ATT, player.YRDS, player.TD, player.INT);
      }
    } else if (statTypeUpper === 'RUSHING') {
      if (player.ATT && player.ATT > 0) {
        player.AVG = parseFloat((player.YRDS / player.ATT).toFixed(2));
      }
    } else if (statTypeUpper === 'RECEIVING') {
      if (player.TRGT && player.TRGT > 0) {
        player['REC%'] = parseFloat(((player.REC / player.TRGT) * 100).toFixed(2));
      }
      if (player.REC && player.REC > 0) {
        player.AVG = parseFloat((player.YRDS / player.REC).toFixed(2));
      }
    } else if (statTypeUpper === 'BLOCKING') {
      if (player.SKA && player.SKA > 0) {
        player.SAD = parseFloat((player.DP / player.SKA).toFixed(2));
        player.SAS = parseFloat((player.DP / player.SKA).toFixed(2));
      }
    } else if (statTypeUpper === 'KICKING') {
      if (player.XPA && player.XPA > 0) {
        player['XP%'] = parseFloat(((player.XPM / player.XPA) * 100).toFixed(2));
      }
      if (player.FGA && player.FGA > 0) {
        player['FG%'] = parseFloat(((player.FGM / player.FGA) * 100).toFixed(2));
      }
      const totalAttempts = (player.XPA || 0) + (player.FGA || 0);
      if (totalAttempts > 0) {
        const totalMakes = (player.XPM || 0) + (player.FGM || 0);
        player['K%'] = parseFloat(((totalMakes / totalAttempts) * 100).toFixed(2));
      }
      player.PNTS = (player.XPM || 0) + ((player.FGM || 0) * 3);
    }
  });

  return result;
};

export const adminStatsService = {
  async insertStats(statType, week, playerData, seasonId) {
    try {
      const tableName = TABLE_NAMES_MAP[statType];

      const dataWithWeekAndSeason = playerData.map(player => ({
        ...player,
        week,
        season_id: seasonId
      }));

      const { data, error } = await supabase
        .from(tableName)
        .insert(dataWithWeekAndSeason)
        .select();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async updateStats(statType, statId, updates) {
    try {
      const tableName = TABLE_NAMES_MAP[statType];

      const { data, error } = await supabase
        .from(tableName)
        .update(updates)
        .eq('id', statId)
        .select();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async deleteStat(statType, statId) {
    try {
      const tableName = TABLE_NAMES_MAP[statType];

      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', statId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getStatsForWeek(statType, week) {
    try {
      const tableName = TABLE_NAMES_MAP[statType];

      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('week', week);

      if (error) {
        console.error(`Database error for ${statType}:`, error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.warn(`No data found for ${statType} in week ${week}`);
        return { success: true, data: [] };
      }

      const normalizedData = data.map(normalizeColumnNames);
      
      const sortedData = normalizedData.sort((a, b) => {
        const nameA = (a.NAME || a.player || '').toLowerCase();
        const nameB = (b.NAME || b.player || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });



      console.log(`Retrieved ${sortedData?.length || 0} ${statType} records for week ${week}`);

      return { success: true, data: sortedData };
    } catch (error) {
      console.error(`Error fetching ${statType} for week ${week}:`, error);
      return { success: false, error: error.message };
    }
  },

  async getAllSeasonStats(statType, seasonId) {
    try {
      if (!statType) {
        const stats = {};
        for (const type of Object.values(STAT_TYPES)) {
          const result = await this.getAllSeasonStats(type, seasonId);
          stats[type] = result;
        }
        return stats;
      }

      const tableName = TABLE_NAMES_MAP[statType];

      let query = supabase
        .from(tableName)
        .select('*')
        .eq('season_id', seasonId)
        .order('week', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) return [];

      const aggregated = aggregatePlayerStats(data, statType);

      return aggregated;
    } catch (error) {
      console.error(`Error fetching ${statType} stats:`, error);
      return [];
    }
  },

  async getPlayersForSeason(seasonId) {
    try {
      const players = new Map();

      for (const statType of Object.values(STAT_TYPES)) {
        const tableName = TABLE_NAMES_MAP[statType];
        
        if (statType === STAT_TYPES.BLOCKING) {
          continue;
        }
        
        const { data, error } = await supabase
          .from(tableName)
          .select('name, team')
          .eq('season_id', seasonId);

        if (!error && data) {
          data.forEach(record => {
            if (record.name) {
              players.set(record.name.toLowerCase(), {
                name: record.name,
                team: record.team || ''
              });
            }
          });
        }
      }

      return Array.from(players.values())
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error fetching players for season:', error);
      return [];
    }
  },

  async getAllWeeksWithData() {
    try {
      const weeks = new Set();

      for (const statType of Object.values(STAT_TYPES)) {
        const tableName = TABLE_NAMES_MAP[statType];
        const { data, error } = await supabase
          .from(tableName)
          .select('week');

        if (!error && data) {
          data.forEach(record => weeks.add(record.week));
        }
      }

      return Array.from(weeks).sort((a, b) => a - b);
    } catch (error) {
      console.error('Error fetching weeks with data:', error);
      return {};
    }
  },

  async getAllSeasons() {
    try {
      const { data, error } = await supabase
        .from('seasons')
        .select('id, season_number, name, year, is_active')
        .order('season_number', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        return [];
      }

      return data;
    } catch (error) {
      console.error('Error fetching seasons:', error);
      return [];
    }
  },

  async getStatsForWeekAndSeason(statType, week, seasonId) {
    try {
      const tableName = TABLE_NAMES_MAP[statType];

      let query = supabase
        .from(tableName)
        .select('*')
        .eq('week', week)
        .eq('season_id', seasonId);

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        return { success: true, data: [] };
      }

      const normalized = data.map(record => normalizeColumnNames(record));

      const sortedData = normalized.sort((a, b) => {
        const nameA = (a.NAME || a.player || '').toLowerCase();
        const nameB = (b.NAME || b.player || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      if (statType.toUpperCase() === 'BLOCKING') {
        sortedData.forEach(player => {
          if (player.SKA && player.SKA > 0) {
            player.SAD = parseFloat((player.DP / player.SKA).toFixed(2));
            player.SAS = parseFloat((player.DP / player.SKA).toFixed(2));
          }
        });
      }

      return {
        success: true,
        data: sortedData
      };
    } catch (error) {
      console.error(`Error fetching ${statType} stats for week ${week} and season ${seasonId}:`, error);
      return { success: false, error: error.message };
    }
  },

  async getNews() {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async insertNews(newsItem) {
    try {
      const { data, error } = await supabase
        .from('news')
        .insert([newsItem])
        .select();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async parseCSV(csvText) {
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length < 2) {
        return { success: false, data: [], error: 'CSV must have headers and data' };
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());

        if (values.length !== headers.length) continue;

        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx];
        });

        data.push(row);
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};
